import React, { useEffect, useRef, useState } from 'react';
import SEOHead from '../SEOHead';
import { countries } from '../../data/countries';
import { normalizeInternationalPhone } from '../../utils/internationalPhone';
import { capturePitruAttribution } from '../../utils/pitruAttribution';
import { recaptchaService } from '../../utils/recaptchaService';
import { trackEvent } from '../../utils/analytics';
import { EXPERIMENT_ID, LeadAnswers, LeadReceipt, leadError, pitruLeadService } from '../../services/pitruLeadService';
import './PitruConsultation.css';

const questions: { key: keyof LeadAnswers; label: string; options: [string, string][] }[] = [
  { key: 'ritualIntent', label: 'What would you like guidance on?', options: [['SHRADDHA', 'Shraddha'], ['PIND_DAAN', 'Pind Daan'], ['TARPAN', 'Tarpan'], ['TRIPINDI', 'Tripindi'], ['PITRA_DOSH_RELATED', 'Pitra Dosh related'], ['UNSURE_NEEDS_GUIDANCE', 'Unsure — I need guidance'], ['OTHER', 'Another ritual need']] },
  { key: 'preferredSacredLocation', label: 'Do you have a preferred sacred location?', options: [['KASHI', 'Kashi'], ['GAYA', 'Gaya'], ['OPEN_TO_GUIDANCE', 'Open to guidance'], ['OTHER', 'Other'], ['UNSURE', 'Unsure']] },
  { key: 'wantsLocationGuidance', label: 'Would you like help choosing the location?', options: [['YES', 'Yes'], ['NO', 'No'], ['UNSURE', 'Unsure']] },
  { key: 'knowsTithi', label: 'Do you know the relevant tithi?', options: [['YES', 'Yes'], ['NO', 'No'], ['UNSURE', 'Unsure']] },
  { key: 'expectedTimeframe', label: 'When are you considering the ritual?', options: [['THIS_PITRU_PAKSHA', 'This Pitru Paksha'], ['WITHIN_THREE_MONTHS', 'Within three months'], ['LATER', 'Later'], ['UNSURE', 'Unsure']] },
  { key: 'priceConsideration', label: 'Would you consider a personalized ritual around ₹15,000–₹30,000?', options: [['YES', 'Yes'], ['POSSIBLY', 'Possibly, after understanding the service'], ['NO_EXPECT_LOWER', 'No, I am looking for a lower price'], ['INFORMATION_ONLY', 'I am only gathering information']] }
];
const empty: LeadAnswers = { name: '', email: '', phone: '', countryOfResidence: '', ritualIntent: '', preferredSacredLocation: '', wantsLocationGuidance: '', knowsTithi: '', expectedTimeframe: '', priceConsideration: '' };

export default function PitruConsultation() {
  const heading = useRef<HTMLHeadingElement>(null);
  // A CTA near the landing-page footer must bring the new form into view.
  useEffect(() => { heading.current?.focus(); }, []);
  const [answers, setAnswers] = useState(empty);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [receipt, setReceipt] = useState<LeadReceipt>();
  const flow = useRef<{ id?: string; events: Set<string>; submitting: boolean }>({ events: new Set(), submitting: false });
  const attribution = useRef(capturePitruAttribution());
  const emit = (name: string) => {
    if (flow.current.events.has(name)) return;
    flow.current.events.add(name);
    trackEvent(name, 'pitru_paksha_v2', undefined, undefined, { experiment_id: EXPERIMENT_ID });
  };
  const change = (key: keyof LeadAnswers, value: string) => {
    emit('pp_v2_qualification_started');
    setAnswers(old => ({ ...old, [key]: value }));
  };
  const next = (event: React.FormEvent) => {
    event.preventDefault();
    if (questions.some(question => !answers[question.key])) { setError('Please answer each question. You can choose an unsure or guidance option.'); return; }
    emit('pp_v2_qualification_completed');
    setError(''); setStep(2);
  };
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (flow.current.submitting || receipt) return;
    let phone: string;
    try { phone = normalizeInternationalPhone(answers.phone); }
    catch (e) { setError((e as Error).message); return; }
    if (!answers.name.trim() || !answers.email.trim() || !answers.countryOfResidence) {
      setError('Please complete your name, email and country of residence.'); return;
    }
    flow.current.submitting = true; setBusy(true); setError('');
    try {
      if (!flow.current.id) flow.current.id = crypto.randomUUID();
      const recaptchaToken = await recaptchaService.getToken('pp_v2_consultation');
      if (!recaptchaToken) { setError('Verification could not load. Please try again.'); return; }
      const result = await pitruLeadService.submit({ ...answers, name: answers.name.trim(), email: answers.email.trim(), phone,
        ...attribution.current, submissionId: flow.current.id, recaptchaToken });
      setReceipt(result);
      emit('pp_v2_consultation_requested');
    } catch (e) { setError(leadError(e)); }
    finally { flow.current.submitting = false; setBusy(false); }
  };

  return <section className="pp-v2-form">
    <SEOHead title="Request an Acharya conversation" description="Request guidance about ancestral rites, including your preferences for Kashi or Gaya. Ritual and location are discussed during consultation." canonicalPath="/pitru-paksha/consultation" noIndex />
    <h1 ref={heading} tabIndex={-1}>Request an Acharya conversation</h1>
    <p className="pp-v2-form__context">This consultation is for families seeking guidance on Pitru Paksha rites, including tithi, ritual type and whether Kashi, Gaya or another approach may be appropriate. There is no obligation to proceed.</p>
    <aside className="pp-v2-form__overview" aria-labelledby="pp-v2-overview-title">
      <h2 id="pp-v2-overview-title">Want to understand the process first?</h2>
      <p>Learn how Antaryatra’s Pitru Paksha consultation works, including Kashi/Gaya guidance, Acharya profiles, typical pricing and what happens after the consultation.</p>
      <a href="/pitru-paksha">View Pitru Paksha overview</a>
    </aside>
    {receipt ? <div role="status">
      <h2>Your consultation request is saved</h2>
      <p>Our team will contact you using the details provided to discuss the next steps. An appointment has not yet been scheduled.</p>
      <p>Reference: <strong>{receipt.id}</strong></p>
    </div> : <>
      <p>Share what you are considering for ancestral rites in Kashi or Gaya. You can be unsure about the ritual or location; our team will discuss both during consultation.</p>
      <p>No payment is collected here. The price range below is a discussion guide, not a quotation.</p>
      <p>Step {step} of 2 · {step === 1 ? 'Your needs' : 'How to reach you'}</p>
      {error && <p role="alert" className="pp-v2-error">{error}</p>}
      {step === 1 ? <form onSubmit={next}>
        {questions.map(question => <label key={question.key}>{question.label}
          <select required value={answers[question.key] || ''} onChange={event => change(question.key, event.target.value)}>
            <option value="">Select an answer</option>
            {question.options.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>)}
        {(answers.ritualIntent === 'OTHER' || answers.preferredSacredLocation === 'OTHER') &&
          <label>Brief context for your other ritual or location (optional)
            <textarea maxLength={500} rows={3} value={answers.broadNeed || ''} onChange={e => change('broadNeed', e.target.value)} />
          </label>}
        <p>You do not need to share ancestor names or detailed family information at this stage.</p>
        <button type="submit">Continue to contact details</button>
      </form> : <form onSubmit={submit}>
        <fieldset disabled={busy}>
          <label>Name<input autoComplete="name" required maxLength={100} value={answers.name} onChange={e => change('name', e.target.value)} /></label>
          <label>Email<input type="email" autoComplete="email" required maxLength={254} value={answers.email} onChange={e => change('email', e.target.value)} /></label>
          <label>Country of residence<select autoComplete="country" required value={answers.countryOfResidence} onChange={e => change('countryOfResidence', e.target.value)}>
            <option value="">Select your country</option>{countries.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
          </select></label>
          <label>Phone with country code<input type="tel" autoComplete="tel" required maxLength={40} aria-describedby="pp-phone-help" value={answers.phone} onChange={e => change('phone', e.target.value)} placeholder="+44 7700 900123" /></label>
          <p id="pp-phone-help">Include + and your calling code, for example +1, +44, +971 or +91. Use the full international number without a domestic trunk prefix or extension.</p>
          <p>By requesting a consultation, you ask our team to contact you about this enquiry. See our <a href="/privacy-policy">Privacy Policy</a>.</p>
          <div className="pp-v2-actions"><button type="button" onClick={() => { setStep(1); setError(''); }}>Back</button><button type="submit">{busy ? 'Saving request…' : 'Request consultation'}</button></div>
        </fieldset>
      </form>}
    </>}
  </section>;
}
